// Libraries
import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { matchRoutes, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Container, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { AxiosError } from 'axios';
import { red } from '@mui/material/colors';

// Components
import SimpleModal from '../Modals/SimpleModal';
import Form from '../Form/Form';
import { loginFields, SUBMIT_BTN_TYPE } from '../Form/form.utils';
import { FormField } from '../../lib/interfaces/Form';

// Utils
import {
	setForgotPasswordModalOpen,
	setLoginModalOpen,
	setRegistrationModalOpen,
} from '../../redux/reducers/ui/modalsReducer';
import { login, googleLogin } from '../../api/apis';
import { UserDTO, UserGoogleLogin, UserRO } from '../../lib/interfaces/User';
import { useAppDispatch } from '../../hooks/redux';
// import useUserMeQuery from '../../reactQuery/hooks/useUserMeQuery';

// CSS
import './AuthModal.scss';
import reactQueryKeys from '../../reactQuery/reactQueryKeys';
import paths from '../../lib/consts/paths';
import { LANGUAGES } from '../../lib/consts/languages';

const LoginModal: FC = () => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const currentLocation = useLocation();
	const { t, i18n } = useTranslation();
	const [showError, setShowError] = useState<boolean>(false);
	const loginFormFields = loginFields.map((field: FormField) => {
		if (field.label) {
			field.label = t(field.label);
		}
		return field;
	});
	const loginMutation = useMutation<UserRO, AxiosError, UserDTO>((data) =>
		login(data),
	);
	const googleLoginMutation = useMutation<UserRO, AxiosError, UserGoogleLogin>(
		(data: UserGoogleLogin) => googleLogin(data),
	);

	const handleClose = useCallback(() => {
		dispatch(setLoginModalOpen(false));
	}, [dispatch]);

	const handleOnSuccessLogin = (data: UserRO) => {
		localStorage.setItem('currentUser', JSON.stringify(data.user));
		localStorage.setItem('token', JSON.stringify(data.token));
		handleClose();
		queryClient.setQueryData(reactQueryKeys.userMe, () => ({ ...data.user }));
		const matches = matchRoutes([{ path: '/' }], currentLocation);
		if (matches) {
			navigate(paths.courses);
		}
	};

	const handleSubmit = (formValuesL: UserDTO) => {
		setShowError(false);
		loginMutation.mutate(formValuesL, {
			onSuccess(data: UserRO) {
				handleOnSuccessLogin(data);
			},
			onError(error) {
				if (error?.response?.status === 401) {
					setShowError(true);
				}
			},
		});
	};

	const register = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		dispatch(setRegistrationModalOpen(true));
		dispatch(setLoginModalOpen(false));
	};

	const handleForgotPasswordClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		dispatch(setLoginModalOpen(false));
		dispatch(setForgotPasswordModalOpen(true));
	};

	const handleGoogleLoginSuccess = (credentialResponse: CredentialResponse) => {
		if (credentialResponse.credential) {
			googleLoginMutation.mutate(
				{
					token: credentialResponse.credential,
					language: i18n.language as LANGUAGES,
				},
				{
					onSuccess(data) {
						handleOnSuccessLogin(data);
					},
					onError(error) {
						if (error?.response?.status === 401) {
							setShowError(true);
						}
					},
				},
			);
		} else {
			setShowError(true);
		}
	};

	const content: React.ReactElement = (
		<Container component="main" maxWidth="xs">
			<div className="AuthModal__wrapper">
				<div className="AuthModal__title">
					<Avatar className="AuthModal__avatar">
						<LockOutlined />
					</Avatar>
					<Typography component="h1" variant="h5">
						{t('login')}
					</Typography>
				</div>
				<div className="AuthModal__social">
					<GoogleLogin
						onSuccess={handleGoogleLoginSuccess}
						onError={() => {
							setShowError(true);
						}}
					/>
				</div>
				<div className="AuthModal__divider">{t('or')}</div>
				<Form
					fields={loginFormFields}
					onSubmit={handleSubmit}
					saveButtonName={t('login')}
					isLoading={loginMutation.isLoading}
					saveButtonStyle={SUBMIT_BTN_TYPE.FULL}
					fullWidth
				/>
				{showError && (
					<Typography component="p" variant="body1" color={red[500]}>
						{t('invalidCredentials')}
					</Typography>
				)}
				<div className="AuthModal__extra-options">
					<a href="register" onClick={(e) => register(e)}>
						{t('dontHaveAnAccount')}
					</a>

					<a href="forgotPassword" onClick={(e) => handleForgotPasswordClick(e)}>
						{t('forgotPassword')}
					</a>
				</div>
			</div>
		</Container>
	);

	return <SimpleModal handleClose={handleClose} content={content} hideActions />;
};

export default LoginModal;
